import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'

const useSolanaActivity = (mimicId, daysAgo) => {
  const [page, setPage] = useState(1)
  const [accumulatedData, setAccumulatedData] = useState([])
  const [total, setTotal] = useState(0)

  // Reset when filters change
  useEffect(() => {
    setPage(1)
    setAccumulatedData([])
    setTotal(0)
  }, [mimicId, daysAgo])

  const { isLoading, isError, isFetching } = useQuery(
    ['useSolanaActivity', mimicId, daysAgo, page],
    () => fetchSolanaActivityPage(mimicId, daysAgo, page),
    {
      enabled: !!mimicId,
      retry: 2,
      staleTime: 1000 * 60 * 5,
      onSuccess: ({ items, total: newTotal }) => {
        setTotal(newTotal)
        setAccumulatedData(prev => page === 1 ? items : [...prev, ...items])
      }
    }
  )

  const hasMore = accumulatedData.length < total
  const loadMore = () => setPage(p => p + 1)

  return {
    data: accumulatedData,
    isLoading: isLoading && page === 1,
    isLoadingMore: isFetching && page > 1,
    isError,
    hasMore,
    loadMore
  }
}

const fetchSolanaActivityPage = async (mimicId, daysAgo, page) => {
  if (!mimicId) return { items: [], total: 0, limit: 200 }

  const endDate = new Date()
  const startDate = new Date()
  startDate.setDate(startDate.getDate() - (daysAgo || 30))

  const params = new URLSearchParams({
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString()
  })

  try {
    const response = await fetch(
      `https://api.mimic.fi/public/solana/client/${mimicId}/summary?${params}&page=${page}`
    )
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)

    const { items: rawItems, total, limit } = await response.json()

    // Collect unique token addresses
    const uniqueTokenAddresses = new Set()
    rawItems.forEach(item => {
      if (item.in?.token) uniqueTokenAddresses.add(item.in.token)
      if (item.out?.token) uniqueTokenAddresses.add(item.out.token)
    })

    try {
      await fetchTokensFromJupiter(Array.from(uniqueTokenAddresses))
    } catch (error) {
      console.warn('Pre-fetch of tokens failed, will use fallback:', error.message)
    }

    const items = await Promise.all(
      rawItems.map(item => item.type === 'swap' ? transformSolanaSwap(item) : transformSolanaActivity(item))
    )

    return { items, total, limit }
  } catch (error) {
    console.error('Error fetching Solana activity:', error)
    throw error
  }
}

const transformSolanaSwap = async (swap) => {
  const executedAt = Math.floor(new Date(swap.day).getTime() / 1000)

  const inTokenInfo = await getTokenInfo(swap.in?.token)
  const outTokenInfo = await getTokenInfo(swap.out?.token)

  return {
    id: swap.signature,
    executedAt,
    feeCollector: swap.feeCollector,
    gasUsed: '0',
    gasPrice: '0',
    costUSD: 0,
    succeeded: swap.status === 'confirmed',
    task: {
      id: `solana-swap-${swap.signature}`,
      name: 'Solana Swap',
      executionType: 'swap',
      taskConfig: JSON.stringify({
        inToken: swap.in?.token,
        outToken: swap.out?.token,
        signature: swap.signature
      })
    },
    transaction: {
      hash: swap.signature,
      sender: 'solana-client'
    },
    movements: [
      {
        added: false,
        amount: `-${swap.in?.amount || '0'}`,
        token: {
          id: swap.in?.token || '',
          symbol: inTokenInfo.symbol,
          name: inTokenInfo.name,
          decimals: swap.in?.decimals ?? inTokenInfo.decimals
        }
      },
      {
        added: true,
        amount: swap.out?.amount || '0',
        token: {
          id: swap.out?.token || '',
          symbol: outTokenInfo.symbol,
          name: outTokenInfo.name,
          decimals: swap.out?.decimals ?? outTokenInfo.decimals
        }
      }
    ],
    smartVault: {
      environment: {
        network: 'solana'
      }
    },
    solanaData: {
      slot: swap.slot,
      signature: swap.signature,
      inPrice: swap.in?.price || 0,
      outPrice: swap.out?.price || 0,
      inAmountUsd: swap.in?.amountUsd || 0,
      outAmountUsd: swap.out?.amountUsd || 0
    }
  }
}

const transformSolanaActivity = async (item) => {
  const executedAt = Math.floor(new Date(item.day).getTime() / 1000)
  const isDeposit = item.type === 'depositor'
  const tokenData = isDeposit ? item.in : item.out
  const tokenInfo = await getTokenInfo(tokenData?.token)

  return {
    id: `${item.slot}-${item.type}`,
    executedAt,
    feeCollector: item.feeCollector,
    gasUsed: '0',
    gasPrice: '0',
    costUSD: 0,
    succeeded: item.status === 'confirmed',
    task: {
      id: `solana-${item.type}-${item.slot}`,
      name: isDeposit ? 'Solana Depositor' : 'Solana Withdraw',
      executionType: item.type,
      taskConfig: JSON.stringify({ token: tokenData?.token })
    },
    transaction: {
      hash: null,
      sender: 'solana-client'
    },
    movements: [
      {
        added: isDeposit,
        amount: isDeposit ? tokenData?.amount || '0' : `-${tokenData?.amount || '0'}`,
        token: {
          id: tokenData?.token || '',
          symbol: tokenInfo.symbol,
          name: tokenInfo.name,
          decimals: tokenData?.decimals ?? tokenInfo.decimals
        }
      }
    ],
    smartVault: {
      environment: {
        network: 'solana'
      }
    },
    solanaData: {
      slot: item.slot,
      count: tokenData?.count,
      amountUsd: tokenData?.amountUsd
    }
  }
}

// Cache for tokens obtained from Jupiter API (per-token cache)
const tokenCache = new Map()
const cacheTimestamp = new Map()
const CACHE_DURATION = 1000 * 60 * 60 // 1 hour

// Known token mapping as fallback
const TOKEN_FALLBACK = {
  'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v': { symbol: 'USDC', name: 'USD Coin', decimals: 6 },
  'So11111111111111111111111111111111111111112': { symbol: 'SOL', name: 'Solana', decimals: 9 },
  'Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB': { symbol: 'USDT', name: 'Tether USD', decimals: 6 },
}

let fullTokenListCache = null
let fullTokenListTimestamp = null
let isFetchingTokens = false
let fetchPromise = null

const fetchTokensFromJupiter = async (tokenAddresses = null) => {
  const now = Date.now()

  if (tokenAddresses && tokenAddresses.length > 0) {
    const tokensToFetch = tokenAddresses.filter(address => {
      const cached = tokenCache.get(address)
      const timestamp = cacheTimestamp.get(address)
      return !cached || !timestamp || (now - timestamp) >= CACHE_DURATION
    })

    if (tokensToFetch.length === 0) return

    if (!fullTokenListCache || !fullTokenListTimestamp || (now - fullTokenListTimestamp) >= CACHE_DURATION) {
      if (isFetchingTokens && fetchPromise) {
        try { await fetchPromise } catch (error) {}
      } else if (!isFetchingTokens) {
        isFetchingTokens = true
        fetchPromise = (async () => {
          let timeoutId = null
          try {
            const controller = new AbortController()
            timeoutId = setTimeout(() => controller.abort(), 10000)

            const response = await fetch('https://quote-api.jup.ag/v6/tokens', {
              signal: controller.signal
            })

            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)

            const tokens = await response.json()
            const tokenMap = {}
            tokens.forEach(token => {
              tokenMap[token.address] = {
                symbol: token.symbol,
                name: token.name,
                decimals: token.decimals,
                logoURI: token.logoURI
              }
            })

            fullTokenListCache = tokenMap
            fullTokenListTimestamp = now
            return tokenMap
          } catch (error) {
            console.warn('Failed to fetch tokens from Jupiter API, using fallback:', error.message)
            fullTokenListCache = {}
            fullTokenListTimestamp = now
            return {}
          } finally {
            if (timeoutId) clearTimeout(timeoutId)
            isFetchingTokens = false
            fetchPromise = null
          }
        })()

        try { await fetchPromise } catch (error) {}
      }
    }

    if (fullTokenListCache) {
      tokensToFetch.forEach(address => {
        const tokenInfo = fullTokenListCache[address]
        if (tokenInfo) {
          tokenCache.set(address, tokenInfo)
          cacheTimestamp.set(address, now)
        }
      })
    }

    return
  }

  const result = {}
  tokenCache.forEach((value, key) => { result[key] = value })
  return result
}

const getTokenInfo = async (tokenAddress) => {
  if (!tokenAddress) return { symbol: 'UNKNOWN', name: 'Unknown Token', decimals: 6 }

  const now = Date.now()
  const cached = tokenCache.get(tokenAddress)
  const timestamp = cacheTimestamp.get(tokenAddress)

  if (cached && timestamp && (now - timestamp) < CACHE_DURATION) return cached

  try {
    await fetchTokensFromJupiter([tokenAddress])
    const jupiterToken = tokenCache.get(tokenAddress)
    if (jupiterToken) return jupiterToken
  } catch (error) {}

  const fallbackToken = TOKEN_FALLBACK[tokenAddress]
  if (fallbackToken) return fallbackToken

  return {
    symbol: `${tokenAddress.slice(0, 4)}...${tokenAddress.slice(-4)}`,
    name: 'Unknown Token',
    decimals: 6
  }
}

export default useSolanaActivity
